import React, { useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Form } from 'antd';

import { PayoutMethodTypes } from 'api/payoutMethods';
import { PayoutMethod } from 'api/types/payout';

import { Button, Checkbox, FormControl, Spinner, TextField, Typography } from 'ui-kit';
import StripeActionButton from './StripeActionButton';

import './BasePayoutMethodForm.scss';
import StripeCountrySelect from './StripeCountrySelect';
import { defaultCountry } from '../../../utils/geo';

interface IBasePayoutMethodForm {
  mode: 'edit' | 'create';
  type: 'STRP' | 'PAYP';
  onSubmit: (data?: {
    nickname: string;
    accountId?: string;
    country?: string | null;
    isDefault?: boolean;
  }) => Promise<void>;
  initialPayoutMethod?: PayoutMethod;
}

const BasePayoutMethodForm = ({
  mode,
  type,
  onSubmit,
  initialPayoutMethod,
}: IBasePayoutMethodForm) => {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    watch,
    setValue,
  } = useForm({
    mode: 'onChange',
    defaultValues: { ...initialPayoutMethod, country: defaultCountry },
  });
  const formValues = watch();

  const disabledConnectButton = useMemo(() => {
    if (!formValues.nickname || isSubmitting) {
      return true;
    }

    return false;
  }, [formValues, isSubmitting]);

  const isStripeOnboardingComplete = useMemo(
    () => initialPayoutMethod?.status === 'COMP',
    [initialPayoutMethod],
  );

  const changeCountry = useCallback(
    ({ value }) => setValue('country', value, { shouldDirty: true }),
    [setValue],
  );

  return (
    <form className="PayoutMethodForm" onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name="nickname"
        render={({ field: { ref, ...rest } }) => (
          <TextField
            label="Nickname"
            required
            fullWidth
            placeholder="e.g. My Stripe Account"
            inputRef={ref}
            inputProps={{
              maxLength: 50,
            }}
            {...rest}
          />
        )}
      />
      {/* STRIPE INTERNATIONAL PAYOUTS */}
      {type === PayoutMethodTypes.STRIPE && mode === 'create' && (
        <>
          <Controller
            control={control}
            name="country"
            render={({ field: { value }, fieldState }) => (
              <Form.Item
                label="Country"
                className="PayoutMethodForm-country-field"
                help={fieldState.error?.message}
              >
                <StripeCountrySelect onChange={changeCountry} value={value} />
              </Form.Item>
            )}
          />
          <div>
            <Typography variant="caption">
              *When you choose a country other than <strong>United States</strong>, you will be
              prompted to accept the{' '}
              <a
                href="https://stripe.com/en-bg/legal/connect-account/recipient"
                target="_blank"
                rel="noreferrer"
              >
                <strong>Stripe Recipient Agreement </strong>
              </a>
              during the Stripe onboarding process.
            </Typography>
          </div>
          <div>
            <Typography variant="caption">
              *Some bank accounts outside the United States, might have payout restrictions. Please
              view{' '}
              <a
                href="https://docs.stripe.com/connect/cross-border-payouts#supported-countries"
                target="_blank"
                rel="noreferrer"
              >
                Stripe supported countries documentation.
              </a>
            </Typography>
          </div>
          <div>
            <Typography variant="caption">
              *Users outside of the USA, click links to see{' '}
              <a
                href="https://docs.stripe.com/connect/cross-border-payouts"
                target="_blank"
                rel="noreferrer"
              >
                supported countries
              </a>{' '}
              and{' '}
              <a href="https://docs.stripe.com/currencies" target="_blank" rel="noreferrer">
                currencies
              </a>
              ,{' '}
              <a
                href="https://docs.stripe.com/currencies/conversions"
                target="_blank"
                rel="noreferrer"
              >
                currency
              </a>{' '}
              conversions, Stripe{' '}
              <a
                href="https://docs.stripe.com/payouts/multicurrency-settlement#multicurrency-settlement-fees"
                target="_blank"
                rel="noreferrer"
              >
                fees,
              </a>
              <a
                href="https://stripe.com/en-bg/legal/connect-account/recipient"
                target="_blank"
                rel="noreferrer"
              >
                recipient notice
              </a>{' '}
              and{' '}
              <a
                href="https://docs.stripe.com/connect/updating-service-agreements#tos-acceptance"
                target="_blank"
                rel="noreferrer"
              >
                service agreement
              </a>
              .
            </Typography>
          </div>
        </>
      )}
      {type === PayoutMethodTypes.PAYPAL && (
        <Controller
          control={control}
          name="accountId"
          render={({ field: { ref, ...rest } }) => (
            <TextField label="Email" required fullWidth inputRef={ref} {...rest} />
          )}
        />
      )}
      {mode === 'edit' && (
        <Controller
          control={control}
          name="isDefault"
          render={({ field: { value, ...rest } }) => (
            <FormControl
              label="Make this my default payout method"
              className="PayoutMethodForm-is-default-field"
            >
              <Checkbox checked={value} {...rest} />
            </FormControl>
          )}
        />
      )}
      <div className="PayoutMethodForm-footer">
        {isSubmitting && <Spinner />}
        {!isSubmitting && mode === 'edit' && (
          <Button variant="contained" color="primary" type="submit">
            Save
          </Button>
        )}
        {!isSubmitting && type === PayoutMethodTypes.STRIPE && (
          <StripeActionButton
            payoutMethodId={initialPayoutMethod?.id}
            mode={mode}
            isOnboardingComplete={isStripeOnboardingComplete}
            disabled={disabledConnectButton}
          />
        )}
      </div>
    </form>
  );
};

export default BasePayoutMethodForm;
